'use client';

import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
import { useUiMotionEnabled } from '../../hooks/useMotion';
import { useHaptic } from '../../hooks/useHaptic';

export interface Segment<T extends string | number> {
  id: T;
  label: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string | number> {
  segments: Segment<T>[];
  value: T;
  onChange: (id: T) => void;
  tone?: 'glass' | 'solid';
  size?: 'sm' | 'md';
  className?: string;
}

const TRACK: Record<'sm' | 'md', string> = {
  sm: 'h-[32px] p-[2px] rounded-full',
  md: 'p-1 rounded-2xl',
};

const SEGMENT: Record<'sm' | 'md', string> = {
  sm: 'h-full px-[10px] rounded-[20px]',
  md: 'h-11 px-4 rounded-xl',
};

const LABEL: Record<'sm' | 'md', string> = {
  sm: 'text-[13.333px] leading-[18px] tracking-[-0.08px]',
  md: 'text-[15px] leading-[20px]',
};

const PLATE: Record<'sm' | 'md', string> = {
  sm: 'rounded-[20px]',
  md: 'rounded-xl',
};

export const SegmentedControl = <T extends string | number>({
  segments,
  value,
  onChange,
  tone = 'glass',
  size = 'sm',
  className,
}: SegmentedControlProps<T>) => {
  const motionEnabled = useUiMotionEnabled();
  const { impact } = useHaptic();
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const [plate, setPlate] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    const active = activeRef.current;
    if (!container || !active) {
      setPlate(null);
      return;
    }
    const left = active.offsetLeft;
    const width = active.offsetWidth;
    setPlate((prev) => (prev && prev.left === left && prev.width === width ? prev : { left, width }));
  }, [segments, value]);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    const active = activeRef.current;
    if (!container || !active) return;
    if (container.scrollWidth <= container.clientWidth) return;
    container.scrollTo({
      left: active.offsetLeft - container.clientWidth / 2 + active.offsetWidth / 2,
      behavior: 'smooth',
    });
  }, [value]);

  const handleSelect = (segment: Segment<T>) => {
    if (segment.disabled || segment.id === value) return;
    impact('soft');
    onChange(segment.id);
  };

  return (
    <div className={cn('w-full', className)}>

      <div
        className={cn(
          'relative overflow-hidden',
          TRACK[size],
          tone === 'glass'
            ? 'liquid-glass backdrop-blur-[15px] backdrop-saturate-[180%] backdrop-brightness-125'
            : 'bg-fill-tertiary'
        )}
      >
        <div
          ref={scrollRef}
          className={cn(
            'relative z-[1] flex h-full items-center gap-[4px]',
            'overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch]',
            '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
          )}
        >

          {plate && (
            <span
              aria-hidden
              className={cn(
                'pointer-events-none absolute top-0 bottom-0 left-0 bg-sc-selected',
                PLATE[size],
                motionEnabled && 'shot-toggle-indicator'
              )}
              style={{ width: `${plate.width}px`, transform: `translateX(${plate.left}px)` }}
            />
          )}
          {segments.map((segment) => {
            const isActive = segment.id === value;
            return (
              <button
                key={segment.id}
                ref={isActive ? activeRef : undefined}
                type="button"
                disabled={segment.disabled}
                onClick={() => handleSelect(segment)}
                className={cn(
                  'relative z-[1] flex-1 min-w-max flex items-center justify-center',
                  SEGMENT[size],
                  'border-none bg-transparent outline-none [-webkit-tap-highlight-color:transparent] transition-colors duration-150',
                  segment.disabled
                    ? 'cursor-default'
                    : 'cursor-pointer active:shadow-[inset_0_0_0_9999px_rgba(255,255,255,0.08)]',
                  !segment.disabled && motionEnabled && 'shot-press-tint',
                  !isActive && !segment.disabled && 'hover:bg-white/[0.04]'
                )}
              >
                <span
                  className={cn(
                    'whitespace-nowrap text-center',
                    LABEL[size],
                    segment.disabled ? 'text-label-tertiary' : 'text-label-primary',
                    isActive ? 'font-[590]' : 'font-[510]'
                  )}
                >
                  {segment.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
